// src/firebase.js
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { isNative } from "./utils/isNative";

// Config Firebase da variabili d'ambiente Vite
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Controllo variabili mancanti (solo log)
const requiredEnvVars = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID",
];

const missingVars = requiredEnvVars.filter((name) => !import.meta.env[name]);
if (missingVars.length > 0) {
  console.error("Missing Firebase env vars:", missingVars);
}

export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

// 🔐 Auth lazily inizializzata (un’unica istanza condivisa)
let _authInstance = null;

async function initializeFirebaseAuth() {
  if (_authInstance) return _authInstance;

  // Native iOS/Android → usiamo initializeAuth con cordovaPopupRedirectResolver
  // per evitare che Firebase provi a caricare gapi.js in WebView
  if (isNative()) {
    console.log(
      "🔵 Native platform detected - Using Cordova resolver to prevent CORS errors",
    );

    const firebaseAuth = await import("firebase/auth");
    const cordova = await import("firebase/auth/cordova");

    _authInstance = firebaseAuth.initializeAuth(app, {
      persistence: [firebaseAuth.indexedDBLocalPersistence],
      popupRedirectResolver: cordova.cordovaPopupRedirectResolver,
    });

    console.log(
      "✅ Firebase Auth initialized with Cordova resolver - no Google gapi scripts will load",
    );
    return _authInstance;
  }

  // 🌐 Web normale → semplice getAuth()
  const firebaseAuth = await import("firebase/auth");
  _authInstance = firebaseAuth.getAuth(app);
  console.log("✅ Firebase Auth initialized with standard getAuth()");
  return _authInstance;
}

// Promise-based getter usato da tutto il resto del codice
export async function getAuthInstance() {
  return await initializeFirebaseAuth();
}

// 🔁 Proxy per compatibilità con il vecchio codice che usa `auth`
export const auth = new Proxy(
  {},
  {
    get(target, prop) {
      // Gestione speciale per onAuthStateChanged → delega ad authHelpers
      if (prop === "onAuthStateChanged") {
        return (callback) => {
          let realUnsubscribe = () => {};

          (async () => {
            try {
              const { setupAuthStateListener } = await import(
                "./utils/authHelpers"
              );
              realUnsubscribe = await setupAuthStateListener(callback);
            } catch (error) {
              console.error(
                "Error setting up auth state listener from proxy:",
                error,
              );
              callback(null);
            }
          })();

          // restituisco la funzione di unsubscribe
          return () => {
            if (realUnsubscribe) realUnsubscribe();
          };
        };
      }

      // Se non è ancora inizializzato
      if (!_authInstance) {
        if (prop === "currentUser") {
          // alcuni componenti leggono auth.currentUser in modo sicuro
          return null;
        }
        throw new Error(
          `Firebase Auth not initialized yet. Tried to access "${prop}". Use getAuthInstance() first.`,
        );
      }

      return _authInstance[prop];
    },
  },
);

// ❗ Nessuna auto-init qui: l’App o i vari helper chiamano getAuthInstance()
